import * as THREE from 'three';
import { CELL, type CityMap } from '../game/map';
import type { StrangerConfig } from '../levels/types';
import type { AnimatedModel } from './CharacterModel';
import { createStrangerFigure, type Figure } from './Figure';
import { wrapAngle } from './Heroine';

export type StrangerState =
  | 'waiting'
  | 'follow'
  | 'stopped'
  | 'decoy'
  | 'retreat'
  | 'shrineWait'
  | 'loiter'
  | 'ahead'
  | 'hidden';

export type StrangerContext = {
  dt: number;
  tension: number;
  act: 1 | 2 | 3;
  playerPos: THREE.Vector3;
  playerForward: THREE.Vector3;
  playerSpeed: number;
  /** Under a streetlight / lit shopfront (not counting her flashlight). */
  playerLit: boolean;
  /** Her phone flashlight is on. */
  flashlight: boolean;
  playerInShrine: boolean;
  lookingBack: boolean;
  /** Whether the player can currently see him (frustum + line of sight), from last frame. */
  visible: boolean;
  field: Int16Array;
  map: CityMap;
  rng: () => number;
  trailPointBehind: (distance: number, out: THREE.Vector3) => boolean;
  isPointVisible: (p: THREE.Vector3) => boolean;
};

export type StrangerEvents = {
  caught: boolean;
  stoppedWithHer: boolean;
  phonePose: boolean;
  vanished: boolean;
  reappeared: boolean;
  waitedAtShrine: boolean;
  /** He was holding a spot (waiting for her) and has now seen her. */
  noticed: boolean;
};

const CHASE_TENSION = 0.92;
/** Seconds she can go without checking behind her before he starts creeping up. */
const UNWATCHED_GRACE = 10;
/** Seconds after the grace period until the creep is at full strength. */
const UNWATCHED_RAMP = 12;

export class Stranger {
  readonly figure: Figure;
  readonly group: THREE.Group;
  private readonly phoneScreen: THREE.MeshBasicMaterial;
  /** Phone glow; kept in the scene (not his group, which hides) so the light count never changes. */
  private readonly phoneLight = new THREE.PointLight('#ff2a1e', 0, 2.2, 2);
  readonly sceneLights = new THREE.Group();
  private model: AnimatedModel | null = null;
  private modelProp: THREE.Group | null = null;
  private phoneBlend = 0;
  private reaching = false;
  private readonly tmpHand = new THREE.Vector3();
  state: StrangerState = 'waiting';
  gap = 40;
  gapTarget = 40;
  speed = 0;
  heading = 0;
  /** Seconds left of closing in faster (she ignored a text). */
  boostTime = 0;
  /** Seconds since she last checked on him (looked back, or had him in view). */
  unwatched = 0;
  private cfg: Pick<StrangerConfig, 'walkMax' | 'chaseSpeed' | 'gapMax'> = { walkMax: 2.8, chaseSpeed: 3.0, gapMax: 40 };
  private stateTime = 0;
  private hideFor = 0;
  private resumeDelay = 0;
  private phonePose = false;
  private holding = false;
  /** Just watching her go by (a calm night): he never follows, and is gone once he moves. */
  private watchOnly = false;
  private watchSawHer = false;
  private decoyDir = new THREE.Vector2();
  private notVisibleTime = 0;
  private playerWasMoving = true;
  private phase = 0;
  private stepCount = 0;
  private shrineExitTime = 0;
  private readonly target = new THREE.Vector3();
  private readonly tmp = new THREE.Vector3();
  readonly events: StrangerEvents = {
    caught: false,
    stoppedWithHer: false,
    phonePose: false,
    vanished: false,
    reappeared: false,
    waitedAtShrine: false,
    noticed: false,
  };
  onStep: ((running: boolean) => void) | null = null;

  constructor() {
    const { figure, phoneScreen } = createStrangerFigure();
    this.figure = figure;
    this.group = figure.root;
    this.phoneScreen = phoneScreen;
    this.sceneLights.add(this.phoneLight);
    this.group.visible = false;
  }

  get position(): THREE.Vector3 {
    return this.group.position;
  }

  /** Where his head is (for the phone portrait). */
  headWorld(out: THREE.Vector3): THREE.Vector3 {
    return this.model ? this.model.headWorld(out) : this.group.localToWorld(out.set(0, 1.62, 0));
  }

  get isPresent(): boolean {
    return this.state !== 'waiting' && this.state !== 'hidden';
  }

  reset(): void {
    this.state = 'waiting';
    this.group.visible = false;
    this.watchOnly = false;
    this.gap = 40;
    this.gapTarget = 40;
    this.speed = 0;
    this.stateTime = 0;
    this.phonePose = false;
    this.notVisibleTime = 0;
    this.playerWasMoving = true;
    this.shrineExitTime = 0;
    this.boostTime = 0;
    this.unwatched = 0;
    this.phoneBlend = 0;
    this.reaching = false;
    this.holding = false;
    this.model?.resetPose();
    if (this.modelProp) this.modelProp.visible = false;
  }

  /**
   * Per-night tuning. He walks too: even closing in he only out-paces her
   * brisk walk (2.6 m/s) a little.
   */
  configure(cfg: Pick<StrangerConfig, 'walkMax' | 'chaseSpeed' | 'gapMax'>): void {
    this.cfg = { ...cfg };
  }

  /** 0..1: how much he's creeping up because she hasn't looked back. */
  get creep(): number {
    return THREE.MathUtils.clamp((this.unwatched - UNWATCHED_GRACE) / UNWATCHED_RAMP, 0, 1);
  }

  /** He walks faster and keeps less distance for a while. */
  boost(seconds: number): void {
    this.boostTime = Math.max(this.boostTime, seconds);
  }

  /**
   * He is already standing somewhere ahead of her (a junction she must pass).
   * Only call when that spot is out of her sight.
   */
  /**
   * Stand somewhere ahead of her. With `hold`, he stays put (no time-out) until
   * she comes close or walks into the light near him.
   */
  appearAhead(x: number, z: number, face: THREE.Vector3, hold = false, watchOnly = false): void {
    this.group.position.set(x, 0, z);
    this.faceToward(face, 1);
    this.group.visible = true;
    this.speed = 0;
    this.holding = hold;
    this.watchOnly = watchOnly;
    this.watchSawHer = false;
    this.setState('ahead');
  }

  spawn(x: number, z: number, heading: number): void {
    this.group.position.set(x, 0, z);
    this.heading = heading;
    this.group.rotation.y = heading;
    this.group.visible = true;
    this.setState('follow');
  }

  /** Called when the player turns at a junction: early on he may walk straight past. */
  tryDecoy(ctx: StrangerContext): boolean {
    if (this.state !== 'follow' || ctx.tension > 0.35 || ctx.act === 3) return false;
    if (ctx.rng() > 0.45) return false;
    const fx = -Math.sin(this.heading);
    const fz = -Math.cos(this.heading);
    if (Math.abs(fx) > Math.abs(fz)) this.decoyDir.set(Math.sign(fx), 0);
    else this.decoyDir.set(0, Math.sign(fz));
    this.setState('decoy');
    return true;
  }

  private setState(state: StrangerState): void {
    if (state === 'hidden' && this.state !== 'hidden') this.events.vanished = true;
    this.state = state;
    this.stateTime = 0;
    if (state === 'loiter') this.phonePose = true;
    else if (state !== 'stopped') this.phonePose = false;
  }

  update(ctx: StrangerContext): void {
    const ev = this.events;
    ev.caught = ev.stoppedWithHer = ev.phonePose = ev.vanished = ev.reappeared = ev.waitedAtShrine = ev.noticed = false;
    const { dt, tension: T, map } = ctx;
    // Only watching tonight: once he stops standing there (she has gone past, or
    // walked up to him and he turned away), he is simply gone. He never follows.
    if (this.watchOnly && this.state !== 'ahead' && this.state !== 'retreat') {
      this.watchOnly = false;
      this.setState('waiting');
      this.group.visible = false;
      return;
    }
    this.boostTime = Math.max(0, this.boostTime - dt);
    const boosted = this.boostTime > 0;
    this.stateTime += dt;
    this.notVisibleTime = ctx.visible ? 0 : this.notVisibleTime + dt;
    if (ctx.playerInShrine) this.shrineExitTime = 0;
    else this.shrineExitTime += dt;

    // She has to keep checking on him: the longer she doesn't, the bolder he gets.
    // Looking back (or having him in view) winds it back down quickly.
    if (ctx.lookingBack || ctx.visible) this.unwatched = Math.max(0, this.unwatched - dt * 8);
    else if (this.state === 'follow' || this.state === 'stopped') this.unwatched += dt;
    const creep = this.creep;

    // Her flashlight is easy to follow: he lets the distance shrink (but walks no faster).
    this.gapTarget =
      T >= CHASE_TENSION
        ? 0
        : (2.5 + this.cfg.gapMax * Math.pow(1 - T, 1.15)) * (boosted ? 0.6 : 1) * (ctx.flashlight ? 0.65 : 1) * (1 - 0.4 * creep);
    if (this.state === 'waiting') return;

    const p = this.group.position;
    const euclid = Math.hypot(p.x - ctx.playerPos.x, p.z - ctx.playerPos.z);
    if (this.state === 'hidden') {
      this.gap = this.gapTarget;
    } else {
      const cell = map.cellAt(p.x, p.z);
      const f = map.inBounds(cell.c, cell.r) ? ctx.field[map.index(cell.c, cell.r)] : -1;
      const pathGap = f <= 1 ? euclid : (f - 0.5) * CELL;
      this.gap += (pathGap - this.gap) * Math.min(1, dt * 4);
    }

    // Light changes how well he can track her.
    // - Flashlight: he just follows the light (normal pace; the gap target shrinks instead).
    // - Dark: he loses her and slows right down, so even at her careful pace she gains ground.
    // - Streetlights: early on he hangs back; near home they expose her.
    let mod: number;
    if (ctx.flashlight) mod = 1;
    else if (!ctx.playerLit) mod = 0.6;
    else mod = ctx.act === 3 ? 1.2 : 0.92;
    if (boosted) mod *= 1.3;
    mod *= 1 + 0.45 * creep;

    const playerMoving = ctx.playerSpeed > 0.35;
    const playerJustStopped = this.playerWasMoving && !playerMoving;
    this.playerWasMoving = playerMoving;

    const chasing = T >= CHASE_TENSION;
    let desiredSpeed = 0;
    let moveToward: THREE.Vector3 | null = null;

    switch (this.state) {
      case 'follow': {
        if (this.gap > 85) {
          this.hideFor = 4;
          this.setState('hidden');
          break;
        }
        if (!chasing && playerJustStopped && T < 0.6 && ctx.rng() < 0.75) {
          this.setState('stopped');
          this.resumeDelay = 0.5 + ctx.rng() * 0.8;
          if (ctx.visible) ev.stoppedWithHer = true;
          break;
        }
        if (!chasing && ctx.lookingBack && ctx.visible && T < 0.5 && euclid < 38) {
          this.setState('stopped');
          this.phonePose = true;
          this.resumeDelay = 1.6 + ctx.rng() * 1.5;
          ev.phonePose = true;
          break;
        }
        // She is walking straight at him.
        this.tmp.set(p.x - ctx.playerPos.x, 0, p.z - ctx.playerPos.z);
        const toward = euclid > 0.01 ? this.tmp.dot(ctx.playerForward) / euclid : 0;
        if (!chasing && euclid < 10 && toward > 0.6 && playerMoving) {
          if (T < 0.75) {
            this.setState('retreat');
            break;
          }
          desiredSpeed = 0; // he waits for her
          this.faceToward(ctx.playerPos, dt);
          break;
        }
        const next = this.nextWaypoint(ctx);
        const here = map.cellAt(p.x, p.z);
        if (next && !chasing && map.isDeadEnd(next.c, next.r) && !map.isDeadEnd(here.c, here.r)) {
          this.setState('loiter');
          break;
        }
        if (next && ctx.playerInShrine && map.isShrine(next.c, next.r)) {
          this.setState('shrineWait');
          ev.waitedAtShrine = true;
          break;
        }
        const err = this.gap - this.gapTarget;
        const max =
          (chasing ? this.cfg.chaseSpeed : T > 0.85 ? this.cfg.walkMax + 0.1 : this.cfg.walkMax) +
          (boosted && !chasing ? 0.5 : 0) +
          (chasing ? 0 : creep * 0.6);
        desiredSpeed = THREE.MathUtils.clamp(ctx.playerSpeed + err * 0.35, 0, max) * (chasing ? 1 : mod);
        if (!playerMoving && !chasing) desiredSpeed = Math.min(desiredSpeed, T < 0.6 ? 0.5 : 1.0);
        moveToward = this.target;
        break;
      }
      case 'stopped': {
        desiredSpeed = 0;
        this.faceToward(ctx.playerPos, dt);
        if (T >= 0.6 && !this.phonePose) {
          this.setState('follow');
          break;
        }
        if (playerMoving && !(this.phonePose && ctx.lookingBack)) {
          this.resumeDelay -= dt;
          if (this.resumeDelay <= 0) this.setState('follow');
        }
        if (this.phonePose && this.stateTime > 6) this.setState('follow');
        break;
      }
      case 'decoy': {
        desiredSpeed = 1.9;
        this.target.set(p.x + this.decoyDir.x * 3, 0, p.z + this.decoyDir.y * 3);
        moveToward = this.target;
        if (this.notVisibleTime > 1.2 || this.stateTime > 9) {
          this.hideFor = 5 + ctx.rng() * 4;
          this.setState('hidden');
        }
        break;
      }
      case 'retreat': {
        // Turn away and drift back the way he came, as if he lives down there.
        const away = this.awayWaypoint(ctx);
        desiredSpeed = 2.4;
        if (away) moveToward = this.target;
        if (this.notVisibleTime > 0.7 || this.stateTime > 7) {
          this.hideFor = 4 + ctx.rng() * 3;
          this.setState('hidden');
        }
        break;
      }
      case 'ahead': {
        // Standing in her way, looking at his phone. Once she is close, or has
        // passed him, he falls in behind her again.
        desiredSpeed = 0;
        this.phonePose = euclid > 12;
        this.faceToward(ctx.playerPos, dt);
        if (this.watchOnly) {
          // Just watching: he stays until she has come by and gone on out of sight.
          if (ctx.visible || euclid < 16) this.watchSawHer = true;
          if (euclid < 7) this.setState('retreat');
          else if ((this.watchSawHer && euclid > 18 && this.notVisibleTime > 6) || this.stateTime > 70) this.setState('follow');
        } else if (this.holding) {
          // Waiting at her building: he notices her if she's close, or lit and near.
          // ...or he gets tired of waiting and goes looking for her.
          if (euclid < 9 || ((ctx.playerLit || ctx.flashlight) && euclid < 20) || this.stateTime > 100) {
            this.holding = false;
            this.events.noticed = true;
            this.setState(T < 0.75 ? 'retreat' : 'follow');
          }
        } else if (euclid < 7) {
          this.setState(T < 0.75 ? 'retreat' : 'follow');
        } else if (this.stateTime > 25 || (this.notVisibleTime > 6 && this.stateTime > 8)) {
          this.setState('follow');
        }
        break;
      }
      case 'loiter': {
        // Waiting where the dead end meets the street, eyes on his phone.
        desiredSpeed = 0;
        this.faceToward(ctx.playerPos, dt);
        const pc = map.cellAt(ctx.playerPos.x, ctx.playerPos.z);
        if (chasing || !map.isDeadEnd(pc.c, pc.r)) {
          this.setState('follow');
          break;
        }
        this.tmp.set(p.x - ctx.playerPos.x, 0, p.z - ctx.playerPos.z);
        const facing = euclid > 0.01 ? this.tmp.dot(ctx.playerForward) / euclid : 0;
        if (T < 0.75 && euclid < 14 && facing > 0.5 && playerMoving) this.setState('retreat');
        break;
      }
      case 'shrineWait': {
        desiredSpeed = 0;
        this.faceToward(ctx.playerPos, dt);
        if (this.notVisibleTime > 2) {
          this.hideFor = 0;
          this.setState('hidden');
        } else if (!ctx.playerInShrine) {
          this.setState('follow');
        }
        break;
      }
      case 'hidden': {
        this.group.visible = false;
        this.speed = 0;
        if (ctx.playerInShrine || this.shrineExitTime < 5) break;
        if (this.stateTime < this.hideFor) break;
        if (this.tryReappear(ctx)) ev.reappeared = true;
        break;
      }
      default:
        break;
    }

    if (this.state !== 'hidden') {
      this.group.visible = true;
      const accel = desiredSpeed > this.speed ? 2.5 : 4;
      this.speed += THREE.MathUtils.clamp(desiredSpeed - this.speed, -accel * dt, accel * dt);
      if (moveToward && this.speed > 0.01) this.moveTo(moveToward, dt, map);

      // Only a real threat once tension is high; before that he steps out of her way.
      const catchable =
        chasing || (T >= 0.75 && (this.state === 'follow' || this.state === 'stopped' || this.state === 'loiter'));
      if (catchable && euclid < (chasing ? 1.35 : 1.1)) {
        ev.caught = true;
      } else if (!catchable && euclid < 1.3 && euclid > 0.001) {
        const push = (1.3 - euclid) / euclid;
        const nx = p.x + (p.x - ctx.playerPos.x) * push;
        const nz = p.z + (p.z - ctx.playerPos.z) * push;
        if (map.isWalkable(nx, nz, 0.9)) p.set(nx, 0, nz);
      }
    }

    this.animate(dt, chasing);
  }

  private nextWaypoint(ctx: StrangerContext): { c: number; r: number } | null {
    const { map, field } = ctx;
    const p = this.group.position;
    const cell = map.cellAt(p.x, p.z);
    const f = map.inBounds(cell.c, cell.r) ? field[map.index(cell.c, cell.r)] : -1;
    if (f <= 1) {
      this.target.copy(ctx.playerPos);
      return null;
    }
    let best: { c: number; r: number } | null = null;
    for (const [dc, dr] of [[0, -1], [0, 1], [1, 0], [-1, 0]]) {
      const nc = cell.c + dc;
      const nr = cell.r + dr;
      if (!map.isPassable(nc, nr)) continue;
      if (field[map.index(nc, nr)] === f - 1) {
        best = { c: nc, r: nr };
        break;
      }
    }
    if (!best) {
      this.target.copy(ctx.playerPos);
      return null;
    }
    map.center(best.c, best.r, this.target);
    return best;
  }

  private awayWaypoint(ctx: StrangerContext): boolean {
    const { map, field } = ctx;
    const p = this.group.position;
    const cell = map.cellAt(p.x, p.z);
    if (!map.inBounds(cell.c, cell.r)) return false;
    const f = field[map.index(cell.c, cell.r)];
    for (const [dc, dr] of [[0, 1], [0, -1], [1, 0], [-1, 0]]) {
      const nc = cell.c + dc;
      const nr = cell.r + dr;
      if (!map.isPassable(nc, nr)) continue;
      if (field[map.index(nc, nr)] === f + 1) {
        map.center(nc, nr, this.target);
        return true;
      }
    }
    // Dead end behind him: stand still facing away.
    this.target.set(p.x * 2 - ctx.playerPos.x, 0, p.z * 2 - ctx.playerPos.z);
    return true;
  }

  private tryReappear(ctx: StrangerContext): boolean {
    for (let extra = 4; extra <= 40; extra += 3) {
      const distance = Math.max(10, this.gapTarget) + extra;
      if (!ctx.trailPointBehind(distance, this.tmp)) return false;
      const cell = ctx.map.cellAt(this.tmp.x, this.tmp.z);
      if (ctx.map.isShrine(cell.c, cell.r)) continue;
      if (!ctx.map.isWalkable(this.tmp.x, this.tmp.z, 1.2)) continue;
      this.tmp.y = 1.2;
      if (ctx.isPointVisible(this.tmp)) continue;
      this.group.position.set(this.tmp.x, 0, this.tmp.z);
      this.faceToward(ctx.playerPos, 1);
      this.gap = distance;
      this.setState('follow');
      this.group.visible = true;
      return true;
    }
    return false;
  }

  private moveTo(target: THREE.Vector3, dt: number, map: CityMap): void {
    const p = this.group.position;
    const dx = target.x - p.x;
    const dz = target.z - p.z;
    const len = Math.hypot(dx, dz);
    if (len < 0.05) return;
    const desired = Math.atan2(-dx, -dz);
    const turn = wrapAngle(desired - this.heading);
    this.heading = wrapAngle(this.heading + turn * Math.min(1, dt * 5));
    this.group.rotation.y = this.heading;
    const step = Math.min(len, this.speed * dt);
    const mx = (dx / len) * step;
    const mz = (dz / len) * step;
    if (map.isWalkable(p.x + mx, p.z + mz, 1.0)) {
      p.x += mx;
      p.z += mz;
    } else if (map.isWalkable(p.x + mx, p.z, 1.0)) {
      p.x += mx;
    } else if (map.isWalkable(p.x, p.z + mz, 1.0)) {
      p.z += mz;
    } else {
      p.x += mx;
      p.z += mz;
    }
  }

  private faceToward(target: THREE.Vector3, dt: number): void {
    const p = this.group.position;
    const desired = Math.atan2(-(target.x - p.x), -(target.z - p.z));
    const turn = wrapAngle(desired - this.heading);
    this.heading = wrapAngle(this.heading + turn * Math.min(1, dt * 3));
    this.group.rotation.y = this.heading;
  }

  private animate(dt: number, chasing: boolean): void {
    const runBlend = THREE.MathUtils.clamp((this.speed - 3.6) / 1.1, 0, 1);
    this.phase += this.speed * dt * (2.3 - runBlend * 0.5);
    const steps = Math.floor(this.phase / Math.PI);
    if (steps !== this.stepCount) {
      this.stepCount = steps;
      if (this.speed > 0.4 && this.group.visible) this.onStep?.(runBlend > 0.4);
    }
    const stride = Math.min(1.1, this.speed / 2.4);
    // Hands stay in pockets until he starts to run.
    const armSwing = chasing ? 0.9 : 0.12;
    this.figure.pose(this.phase, stride, runBlend, armSwing, 0, this.phonePose ? 0.45 : 0.05);
    this.figure.armL.rotation.z = chasing ? 0 : -0.1;
    if (this.phonePose) {
      this.figure.armR.rotation.x = 1.2;
      this.figure.armR.rotation.z = -0.3;
    } else {
      this.figure.armR.rotation.z = chasing ? 0 : 0.1;
    }
    this.figure.propHand.visible = this.phonePose;
    this.phoneLight.intensity = this.phonePose && this.group.visible ? 1.1 : 0;
    if (this.phoneLight.intensity > 0) {
      (this.modelProp ?? this.figure.propHand).getWorldPosition(this.phoneLight.position);
      this.phoneLight.position.y += 0.1;
    }

    if (this.model && this.modelProp) {
      const m = this.model;
      m.locomotion(this.speed, 1.9, 2.6, Infinity);
      m.update(dt);
      this.phoneBlend += ((this.phonePose ? 1 : 0) - this.phoneBlend) * Math.min(1, dt * 6);
      m.turnHead(0, this.phoneBlend * 0.5);
      m.raiseRightHand(this.phoneBlend);
      this.placeProp();
    }
  }

  /** Swap the procedural figure for a skinned, animated model. */
  attachModel(model: AnimatedModel): void {
    this.model = model;
    this.figure.body.visible = false;
    this.group.add(model.root);
    const prop = new THREE.Group();
    const body = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.16, 0.012), new THREE.MeshStandardMaterial({ color: '#111', roughness: 0.4 }));
    const screen = new THREE.Mesh(new THREE.PlaneGeometry(0.07, 0.14), this.phoneScreen);
    screen.position.z = 0.007;
    prop.add(body, screen);
    prop.visible = false;
    this.modelProp = prop;
    this.group.add(prop);
    model.resetPose();
  }

  /** The ending: he reaches out and takes hold of her. */
  reachOut(dt: number, t: number): void {
    if (this.model) {
      if (!this.reaching) {
        this.reaching = true;
        this.model.playOnce('Interact', 0.15);
      }
      this.model.update(dt);
      if (this.modelProp) this.modelProp.visible = false;
      return;
    }
    const reach = Math.min(1, t / 0.8);
    this.figure.pose(0, 0, 0, 0, 0, 0.1);
    this.figure.armR.rotation.x = 1.35 * reach;
    this.figure.armR.rotation.z = -0.15 * reach;
  }

  private placeProp(): void {
    const prop = this.modelProp;
    const m = this.model;
    if (!prop || !m) return;
    prop.visible = this.phoneBlend > 0.3 && this.group.visible;
    if (!prop.visible) return;
    this.group.worldToLocal(m.handWorld(this.tmpHand));
    prop.position.copy(this.tmpHand);
    prop.lookAt(m.headWorld(this.tmpHand));
  }

  dispose(): void {
    this.model?.dispose();
    this.figure.dispose();
    this.phoneScreen.dispose();
  }
}
